// 2P AI — configurazione server (§8.6), 1:1 con 2pai-server-config.html. id="new" = creazione.
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { withStepUp } from '../lib/webauthn';
import { useServers } from '../lib/queries';
import { CommandBar } from '../components/CommandBar';
import { Crumb } from '../components/Crumb';
import { Icon } from '../components/Icon';
import { TagList } from '../components/TagList';

interface Reach { cc_present: boolean; cc_version: string | null; node_ok: boolean; engine_reachable: boolean; checked_at: string }

export function ServerConfig({ id }: { id: string }) {
  const isNew = id === 'new';
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const servers = useServers().data ?? [];
  const server = isNew ? undefined : servers.find((s) => s.id === id);

  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [isHub, setIsHub] = useState(false);
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [sshUser, setSshUser] = useState('claude-user');
  const [workingRoots, setWorkingRoots] = useState<string[]>([]);
  const [cpuWarn, setCpuWarn] = useState('85');
  const [diskWarn, setDiskWarn] = useState('90');
  const [redSec, setRedSec] = useState('60');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [reach, setReach] = useState<Reach | null>(null);
  const [reachErr, setReachErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [provBusy, setProvBusy] = useState(false);
  const [provCmd, setProvCmd] = useState<string | null>(null);
  const [provExp, setProvExp] = useState(30);
  const [provErr, setProvErr] = useState<string | null>(null);

  useEffect(() => {
    if (loaded || isNew) { if (isNew) setLoaded(true); return; }
    if (server) {
      setName(server.name);
      setLabel(server.label || '');
      setIsHub(server.role === 'hub');
      setHost(server.host);
      setPort(String(server.port));
      setSshUser(server.ssh_user);
      setWorkingRoots(server.working_roots ?? []);
      setCpuWarn(String(server.cpu_warn ?? 85));
      setDiskWarn(String(server.disk_warn ?? 90));
      setRedSec(String(server.unreachable_red_sec ?? 60));
      setLoaded(true);
    }
  }, [isNew, server, loaded]);

  async function save() {
    setError(null);
    if (!name.trim() || !host.trim()) { setError('Nome e host sono obbligatori.'); return; }
    setBusy(true);
    const body = {
      name: name.trim(),
      label: label.trim() || null,
      role: isHub ? 'hub' : 'remote',
      host: host.trim(),
      port: Number(port) || 22,
      ssh_user: sshUser.trim() || 'claude-user',
      working_roots: workingRoots,
      cpu_warn: Number(cpuWarn) || 85,
      disk_warn: Number(diskWarn) || 90,
      unreachable_red_sec: Number(redSec) || 60,
    };
    try {
      // Server create/edit richiedono lo step-up (add_server/edit_server, api.md §Step-up).
      if (isNew) {
        const res = await withStepUp('add_server', (token) => api.post<{ server: { id: string } }>('/servers', body, token));
        void qc.invalidateQueries({ queryKey: ['servers'] });
        // Vai alla config del server appena creato: lì c'è "Collega questa macchina" (genera il comando).
        navigate(`/server/${res.server.id}/config`);
        return;
      }
      await withStepUp('edit_server', (token) => api.patch<unknown>(`/servers/${id}`, body, token));
      void qc.invalidateQueries({ queryKey: ['servers'] });
      navigate('/server');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio');
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setReach(null); setReachErr(null); setTesting(true);
    try {
      setReach(await api.post<Reach>(`/servers/${id}/reachability`));
    } catch (e) {
      setReachErr(e instanceof Error ? e.message : 'Connessione fallita');
    } finally {
      setTesting(false);
    }
  }

  async function genProvision() {
    setProvErr(null); setProvBusy(true);
    try {
      const r = await withStepUp('add_server', (token) =>
        api.post<{ command: string; expires_in_min: number }>(`/servers/${id}/provision`, {}, token));
      setProvCmd(r.command); setProvExp(r.expires_in_min);
    } catch (e) {
      setProvErr(e instanceof Error ? e.message : 'Errore nella generazione del comando');
    } finally {
      setProvBusy(false);
    }
  }
  function copyCmd() { if (provCmd) void navigator.clipboard?.writeText(provCmd); }

  async function remove() {
    if (!window.confirm('Rimuovere il server da 2P AI? Prima sposta o elimina i progetti che ci stanno sopra.')) return;
    setBusy(true);
    try {
      await withStepUp('edit_server', (token) => api.del(`/servers/${id}`, token));
      void qc.invalidateQueries({ queryKey: ['servers'] });
      navigate('/server');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile rimuovere (progetti collegati?)');
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <CommandBar />
      <div className="form">
        <Crumb current={isNew ? 'Nuovo server' : 'Configurazione'} onBack={() => navigate('/server')} backLabel="Server" />
        <div className="ftitle">{isNew ? 'Nuovo server' : 'Configurazione server'}</div>
        <div className="fsub">Come 2P AI si collega alla macchina e quando il semaforo cambia colore</div>

        <div className="fsection">
          <h3><Icon name="badge" />Identità</h3>
          <div className="field"><label>Nome</label><input className="inp" type="text" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="field"><label>Etichetta</label><input className="inp" type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="OVH · hub principale" /><div className="help">Una descrizione breve che vedi sulla scheda del server.</div></div>
          <div className="row-toggle"><div><div className="tl">Hub principale</div><div className="td">La macchina da cui 2P AI coordina le altre</div></div>
            <button type="button" className={isHub ? 'switch on' : 'switch'} role="switch" aria-checked={isHub} aria-label="Hub principale" onClick={() => setIsHub((v) => !v)} />
          </div>
        </div>

        <div className="fsection">
          <h3><Icon name="lan" />Connessione SSH</h3>
          <div className="grid3">
            <div className="field" style={{ marginBottom: 0 }}><label>Host / indirizzo IP</label><input className="inp mono" type="text" value={host} onChange={(e) => setHost(e.target.value)} /></div>
            <div className="field" style={{ marginBottom: 0 }}><label>Porta</label><input className="inp mono" type="text" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value)} /></div>
          </div>
          <div className="field" style={{ marginTop: 15 }}><label>Utente SSH</label><input className="inp mono" type="text" value={sshUser} onChange={(e) => setSshUser(e.target.value)} /></div>
          <div className="field"><label>Chiave SSH</label>
            <div className="keyrow"><Icon name="key" className="k" /><div className="kt"><div className="l1">Gestita automaticamente dall'installer</div><div className="l2">non devi fare nulla</div></div></div>
            <div className="help">Collegando la macchina con il comando qui sotto, la chiave del tunnel viene creata e installata da sola — non serve inserirla. A te basta poter aprire un terminale come root sul server (SSH o console del provider).</div>
          </div>
          <div className="testrow">
            <button className="btn-ghost-sm" type="button" disabled={isNew || testing} onClick={() => void test()}><Icon name="cable" />{testing ? 'Provo…' : 'Testa connessione'}</button>
            {reach ? <span className="test-ok"><Icon name="check_circle" />Connessione riuscita{reach.cc_version ? ` · CC ${reach.cc_version}` : ''}</span> : null}
            {reachErr ? <span style={{ color: 'var(--err)', fontSize: '12.5px', fontWeight: 600 }}>{reachErr}</span> : null}
          </div>
        </div>

        {!isHub ? (
          <div className="fsection">
            <h3><Icon name="rocket_launch" />Collega questa macchina</h3>
            {isNew ? (
              <p className="help" style={{ marginTop: -2 }}>Prima <b>salva</b> il server (pulsante in fondo). Poi si apre la sua pagina e <b>qui comparirà il comando</b> da incollare sulla macchina per collegarla.</p>
            ) : (<>
            <p className="help" style={{ marginTop: -2 }}>Installa il motore su questo server e lo collega al pannello con <b>un solo comando</b> — via tunnel cifrato, nessuna porta pubblica aperta.</p>
            <button className="btn-ghost-sm" type="button" disabled={provBusy} onClick={() => void genProvision()}><Icon name="terminal" />{provBusy ? 'Genero…' : 'Genera comando di collegamento'}</button>
            {provErr ? <div className="note danger" style={{ marginTop: 10 }}><Icon name="error" /><span>{provErr}</span></div> : null}
            {provCmd ? (
              <div className="vademecum">
                <ol>
                  <li>Entra in questo server come <b>root</b> (SSH dal tuo computer, o la console del provider).</li>
                  <li>Incolla ed esegui questo comando — tutto su una riga (scade tra <b>{provExp} min</b>):</li>
                </ol>
                <div className="cmdbox"><pre>{provCmd}</pre><button type="button" className="btn-ghost-sm" onClick={copyCmd}><Icon name="content_copy" />Copia</button></div>
                <ol start={3}>
                  <li>A fine installazione, se chiede di autenticare Claude Code, esegui sul server:<br /><code>claude auth login --claudeai</code><br />apri l'URL mostrato, autorizza nel browser, incolla il codice.</li>
                  <li>Torna qui e premi <b>Testa connessione</b>: diventa verde quando il motore risponde.</li>
                </ol>
              </div>
            ) : null}
            </>)}
          </div>
        ) : null}

        <div className="fsection">
          <h3><Icon name="folder_managed" />Cartelle consentite</h3>
          <div className="field"><label>Dove Claude Code può operare su questa macchina</label>
            <TagList items={workingRoots} promptText="Percorso assoluto (es. /var/www)" onAdd={(v) => setWorkingRoots((r) => r.includes(v) ? r : [...r, v])} onRemove={(v) => setWorkingRoots((r) => r.filter((x) => x !== v))} />
            <div className="help">Un confine di sicurezza: fuori da queste cartelle Claude Code non può leggere né scrivere, qualunque cosa gli venga chiesto.</div>
          </div>
        </div>

        <div className="fsection">
          <h3><Icon name="monitor_heart" />Semaforo · soglie di salute</h3>
          <div className="thr"><span className="lamp a" /><div className="tlbl"><div className="l1">Diventa giallo se la CPU supera</div><div className="l2">Macchina su ma sotto sforzo</div></div><div className="num"><input type="text" inputMode="numeric" value={cpuWarn} onChange={(e) => setCpuWarn(e.target.value)} /><span className="pct">%</span></div></div>
          <div className="thr"><span className="lamp a" /><div className="tlbl"><div className="l1">Diventa giallo se il disco supera</div><div className="l2">Spazio quasi esaurito</div></div><div className="num"><input type="text" inputMode="numeric" value={diskWarn} onChange={(e) => setDiskWarn(e.target.value)} /><span className="pct">%</span></div></div>
          <div className="thr"><span className="lamp r" /><div className="tlbl"><div className="l1">Diventa rosso se non risponde per</div><div className="l2">Macchina spenta o irraggiungibile</div></div><div className="num"><input type="text" inputMode="numeric" value={redSec} onChange={(e) => setRedSec(e.target.value)} /><span className="pct" style={{ fontSize: 11 }}>sec</span></div></div>
        </div>

        {!isNew ? (
          <div className="danger">
            <h3>Zona delicata</h3>
            <p>Per rimuovere il server, prima sposta o elimina i progetti che ci stanno sopra. Rimuoverlo cancella la connessione da 2P AI, non tocca la macchina.</p>
            <button className="btn-danger" type="button" disabled={busy} onClick={() => void remove()}><Icon name="delete" />Rimuovi server</button>
          </div>
        ) : null}

        {error ? <div className="note danger"><Icon name="error" /><span>{error}</span></div> : null}

        <div className="actionbar">
          <button className="btn-ghost" type="button" onClick={() => navigate('/server')}>Annulla</button>
          <button className="btn-primary" type="button" disabled={busy} onClick={() => void save()}><Icon name="check" />{busy ? '…' : 'Salva'}</button>
        </div>
      </div>
    </div>
  );
}
